import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Icon } from '../../../components/icons/Icon'
import { InlineAlert } from '../../../components/InlineAlert'
import { ErrorState } from '../../../design-system/list-view/states/ErrorState'
import { approveTenantEligibilityOverlay, getTenantEligibilityOverlay } from '../../../api/eligibilityOverlay'
import { ApiError } from '../../../api/problem'
import { recordOverlaySeen } from '../../../lib/eligibilityOverlayRecents'
import { OverlayApproveBar } from './OverlayApproveBar'
import { useOverlayCapabilities } from './useOverlayCapabilities'
import type { DecisionDelta, TenantEligibilityOverlayView } from '../../../types/eligibilityOverlay'

/**
 * IB-289 Verifier fix (S3, honesty bug) — `TenantEligibilityOverlayService.approve()` throws TWO
 * distinct `AccessDeniedException` subtypes that BOTH map to a bare `403 capability-denied` via the
 * shared `kernel.web.ApiExceptionHandler` (same `type`/`title`, no `code` property — re-verified on
 * `main`): `EligibilityOverlaySelfApprovalException` (maker≠checker) and
 * `EligibilityOverlayAccessDeniedException` (missing `eligibility_overlay:A` outright — reachable if
 * a stale capability cache let a disabled Approve control through). Since neither the wire `type`
 * nor a `code` property distinguishes them, the only real discriminator IS the two exceptions' own
 * (stable, hardcoded) message text — matched here, never a generic "any 403 means self-authored"
 * mislabel. Filed as an IB-288 follow-up: add a distinct `code` property so this stops being a
 * substring match.
 */
function isSelfApprovalProblem(detail: string): boolean {
  return detail.includes('cannot be approved by the same actor who authored it')
}

function DeltaList({ title, deltas }: { title: string; deltas: DecisionDelta[] }) {
  if (deltas.length === 0) return null
  return (
    <div>
      <strong style={{ fontWeight: 500, fontSize: 'var(--iw-size-sm)' }}>{title}</strong>
      <ul style={{ margin: 'var(--iw-space-1) 0 0', paddingLeft: 'var(--iw-space-4)' }}>
        {deltas.map((d) => (
          <li key={d.key}>
            <code>{d.key}</code> ({d.kind})
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * `/admin/eligibility-overlays/:overlayId` (design doc §3.2). AC5's honest-labeling surface: status,
 * the pinned `(baseModelId, baseVersion)`, `overlayVersion`, author, and — once approved — approver +
 * timestamp. Hosts `OverlayApproveBar` (AC3). The submitted delta lists render exactly what the
 * server returned (AC2's "no silent merge, no partial-apply" — this IS that proof).
 *
 * <p><b>AC4 (canon-vs-overlay resolution preview) — NOT built, a named backend gap, not a dead
 * button.</b> The merged IB-288 controller exposes no `GET .../resolution-preview` endpoint (see
 * `docs/design/ib-289-tenant-eligibility-overlay-ui-design.md` §5, itself flagged PROPOSED and not
 * taken up). The merge (`EligibilityOverlayMerger`) is deterministic server-side logic this UI must
 * NOT reimplement client-side (one-algorithm-one-place, same discipline as chart palettes/calc
 * paths) — so rather than fabricate a client-side merge or ship a button that 404s, this panel
 * states the gap honestly. Filed as an IB-288 follow-up.
 */
export function OverlayDetailScreen() {
  const { overlayId } = useParams<{ overlayId: string }>()
  const [overlay, setOverlay] = useState<TenantEligibilityOverlayView | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [approveBusy, setApproveBusy] = useState(false)
  const [heldReason, setHeldReason] = useState<string | null>(null)
  const [approveError, setApproveError] = useState<string | null>(null)
  const capabilities = useOverlayCapabilities()

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!overlayId) return
      setLoadError(null)
      getTenantEligibilityOverlay(overlayId, signal)
        .then((view) => {
          if (signal?.aborted) return
          setOverlay(view)
          recordOverlaySeen(view.id)
        })
        .catch((err) => {
          if (signal?.aborted) return
          setOverlay(null)
          setLoadError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load this overlay.')
        })
    },
    [overlayId],
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  async function handleApprove() {
    if (!overlay) return
    setApproveBusy(true)
    setHeldReason(null)
    setApproveError(null)
    try {
      const updated = await approveTenantEligibilityOverlay(overlay.id)
      setOverlay(updated)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        const detail = err.problem.detail ?? err.message
        if (isSelfApprovalProblem(detail)) {
          // AC3 failure path: the real self-approval 403 surfaced inline verbatim; the UI re-syncs
          // to the server's actual (still-draft) state by simply re-loading rather than assuming
          // it knows the state.
          setHeldReason(detail)
          load()
        } else {
          // A DIFFERENT 403 cause (missing eligibility_overlay:A outright, e.g. a stale capability
          // cache let a disabled control through) — never mislabeled as "you authored this
          // overlay"; surfaced as its own honest error instead.
          setApproveError(detail)
          load()
        }
      } else {
        setApproveError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not approve this overlay.')
      }
    } finally {
      setApproveBusy(false)
    }
  }

  if (loadError) {
    return <ErrorState message={loadError} onRetry={() => load()} />
  }

  if (!overlay) {
    return <div style={{ padding: 'var(--iw-space-5)', color: 'var(--iw-text-muted)' }}>Loading…</div>
  }

  return (
    <div style={{ padding: 'var(--iw-space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)', maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        <Icon name="list-tree" size={18} />
        <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Eligibility overlay</h2>
      </div>

      <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: 'var(--iw-space-1) var(--iw-space-3)', margin: 0 }}>
        <dt style={{ color: 'var(--iw-text-muted)' }}>Base model</dt>
        <dd style={{ margin: 0 }}><code>{overlay.baseModelId}</code> @ v{overlay.baseVersion}</dd>
        <dt style={{ color: 'var(--iw-text-muted)' }}>Overlay version</dt>
        <dd style={{ margin: 0 }}>{overlay.overlayVersion}</dd>
        <dt style={{ color: 'var(--iw-text-muted)' }}>Author</dt>
        <dd style={{ margin: 0 }}>{overlay.createdBy}</dd>
      </dl>

      <OverlayApproveBar
        status={overlay.status}
        approvedBy={overlay.approvedBy}
        approvedAt={overlay.approvedAt}
        busy={approveBusy}
        heldReason={heldReason}
        onApprove={() => void handleApprove()}
        capabilityStatus={capabilities.status}
        canApprove={capabilities.canApprove}
        capabilityError={capabilities.error}
      />
      {approveError ? <InlineAlert severity="error">{approveError}</InlineAlert> : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
        <strong style={{ fontWeight: 500 }}>Staged deltas (exactly as submitted)</strong>
        {overlay.addedDecisions.length === 0 && overlay.overriddenDecisions.length === 0 && overlay.removedDecisionKeys.length === 0 ? (
          <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)' }}>No deltas were staged on this overlay.</p>
        ) : (
          <>
            <DeltaList title="Added" deltas={overlay.addedDecisions} />
            <DeltaList title="Overridden" deltas={overlay.overriddenDecisions} />
            {overlay.removedDecisionKeys.length > 0 ? (
              <div>
                <strong style={{ fontWeight: 500, fontSize: 'var(--iw-size-sm)' }}>Removed</strong>
                <ul style={{ margin: 'var(--iw-space-1) 0 0', paddingLeft: 'var(--iw-space-4)' }}>
                  {overlay.removedDecisionKeys.map((key) => (
                    <li key={key}><code>{key}</code></li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>

      <InlineAlert severity="info">
        Canon-vs-overlay resolution preview (as-of a filing period) is not available yet — the backend
        (IB-288) does not currently expose a resolution-preview endpoint, and this UI does not
        reimplement the merge client-side. Filed as a follow-up.
      </InlineAlert>
    </div>
  )
}
